// src/pages/Dashboard.tsx - ENHANCED PROFESSIONAL DASHBOARD
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Fade,
  Chip,
  Paper,
  Divider,
  Avatar,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  LocalGasStation as FuelIcon,
  DirectionsCar as CarIcon,
  Engineering as EngineIcon,
  Timelapse as TimelapseIcon,
  TrendingUp,
  TrendingDown,
  Warning as WarningIcon,
  CheckCircleOutline,
  LocalShipping,
  Commute,
  Engineering,
  LocationOn,
  BatteryChargingFull,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Device } from '../types';
import { analyticsAPI } from '../services/api';
import AddDeviceDialog from '../components/AddDeviceDialog';
import RealDriverSafetyScorecard from '../components/RealDriverSafetyScorecard';
import RealFleetMileageChart from '../components/RealFleetMileageChart';
import RealFuelTrendChart from '../components/RealFuelTrendChart';
import RealEngineHealthWidget from '../components/RealEngineHealthWidget';
import RealIdleTimeWidget from '../components/RealIdleTimeWidget';
import { useRealTimeLocation } from '../hooks/useRealTimeLocation';
import GeofenceAlertsWidget from '../components/GeofenceAlertsWidget';
import SpeedAnalyticsChart from '../components/SpeedAnalyticsChart';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingSteps, setLoadingSteps] = useState<string[]>([]);

  // ✅ React Query - Automatic caching, refetching, and loading states
  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['dashboard-initial-load', '30d'],
    queryFn: () => analyticsAPI.getDashboardInitialLoad('30d'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // ✅ Active Alerts Query
  const { data: alertsData } = useQuery({
    queryKey: ['dashboard-alerts'],

   queryFn: async () => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/active-alerts`, {headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      return result;
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // ✅ Vehicle Performance Query - MOVED TO TOP LEVEL
  const { data: performanceData, isLoading: loadingPerformance } = useQuery({
    queryKey: ['vehicle-performance', '14'],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:1001'}/api/dashboard/vehicle-performance?days=14`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      return result;
    },
    staleTime: 5 * 60 * 1000,
  });

  // ✅ Simulate progressive loading steps
  useEffect(() => {
    if (isLoading || isFetching) {
      setLoadingProgress(0);
      setLoadingSteps([]);

      const steps = [
        { delay: 500, progress: 20, message: '📊 Fetching devices...' },
        { delay: 1200, progress: 40, message: '🔧 Analyzing engine health...' },
        { delay: 2000, progress: 60, message: '📏 Computing mileage trends...' },
        { delay: 2800, progress: 80, message: '⛽ Processing fuel data...' },
        { delay: 3500, progress: 95, message: '✅ Finalizing dashboard...' },
      ];

      steps.forEach(step => {
        setTimeout(() => {
          if (isLoading || isFetching) {
            setLoadingProgress(step.progress);
            setLoadingSteps(prev => [...prev, step.message]);
          }
        }, step.delay);
      });

      const clearTimer = setTimeout(() => {
        setLoadingProgress(100);
      }, 4000);

      return () => {
        clearTimeout(clearTimer);
        setLoadingProgress(0);
        setLoadingSteps([]);
      };
    }
  }, [isLoading, isFetching]);

  useEffect(() => {
    if (dashboardData && !selectedDevice && dashboardData.devices?.length > 0) {
      const firstOnlineDevice = dashboardData.devices.find(d => d.status === 'online');
      if (firstOnlineDevice) {
        setSelectedDevice(firstOnlineDevice);
      }
    }
  }, [dashboardData, selectedDevice]);

  // Real-time location tracking
  const {
    currentLocation,
    isConnected,
    error: locationError,
    isMoving,
    clearError: clearLocationError,
  } = useRealTimeLocation(selectedDevice?.imei);

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
        sx={{ px: 3 }}
      >
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h5" sx={{ mt: 3, fontWeight: 600 }}>
              Loading Fleet Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please wait while we gather your fleet data...
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Chip
                label={`${loadingProgress}%`}
                size="small"
                color="primary"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #0D47A1 0%, #42A5F5 100%)'
                }
              }}
            />
          </Box>

          {/* Loading Steps */}
          <Card variant="outlined" sx={{ bgcolor: 'grey.50', mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Processing Steps:
              </Typography>
              <Box sx={{ mt: 2 }}>
                {[
                  { icon: <CarIcon />, label: 'Fetching devices', color: '#1e88e5' },
                  { icon: <EngineIcon />, label: 'Analyzing engine health', color: '#43a047' },
                  { icon: <SpeedIcon />, label: 'Computing mileage trends', color: '#fb8c00' },
                  { icon: <FuelIcon />, label: 'Processing fuel data', color: '#e53935' },
                  { icon: <TimelapseIcon />, label: 'Finalizing metrics', color: '#8e24aa' },
                ].map((step, index) => {
                  const isCompleted = loadingSteps.length > index;
                  const isCurrent = loadingSteps.length === index + 1;

                  return (
                    <Fade in key={step.label} timeout={500}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderRadius: 1,
                          bgcolor: isCurrent ? 'primary.50' : 'transparent',
                          transition: 'all 0.3s ease',
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            color: isCompleted ? step.color : 'grey.400',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {isCompleted ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            React.cloneElement(step.icon, { fontSize: 'small' })
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            color: isCompleted ? 'text.primary' : 'text.secondary',
                            fontWeight: isCurrent ? 600 : 400,
                          }}
                        >
                          {step.label}
                        </Typography>
                        {isCurrent && (
                          <CircularProgress size={16} thickness={6} />
                        )}
                        {isCompleted && !isCurrent && (
                          <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                        )}
                      </Box>
                    </Fade>
                  );
                })}
              </Box>
            </CardContent>
          </Card>

          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            display="block"
            sx={{ mt: 3 }}
          >
            ⚡ Dashboard data is cached for 5 minutes. Subsequent visits will be instant!
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6">Dashboard Error</Typography>
          <Typography>{(error as Error)?.message || 'Failed to load dashboard data'}</Typography>
          <Button onClick={handleRefresh} sx={{ mt: 2 }} variant="contained">
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  const {
    devices = [],
    summary = null,
    mileage = null,
    fuel = null,
    engine = null,
    idle = null,
    speed = null
  } = (dashboardData as any) || {};

  // ✅ FIXED: Calculate additional metrics with proper null checks using optional chaining
  const utilizationRate = summary?.fleet?.totalVehicles > 0 ?
    Math.round((summary.fleet.healthyVehicles / summary.fleet.totalVehicles) * 100) : 0;

  const avgSpeedKmh = mileage?.dailyBreakdown?.reduce((acc: number, day: any) =>
    acc + (day.avgSpeed || 0), 0) / (mileage?.dailyBreakdown?.length || 1) || 0;

  return (
    <Fade in timeout={500}>
      <Box>
        {/* Enhanced Header with Gradient */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 2, sm: 3 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 3,
            color: 'white',
            boxShadow: '0 8px 32px rgba(13, 71, 161, 0.3)',
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2
          }}>
            <Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Fleet Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Real-time monitoring and analytics • Last updated: {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={isFetching}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAdd(true)}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                  fontWeight: 'bold',
                  boxShadow: 3,
                }}
              >
                Add Device
              </Button>
            </Box>
          </Box>
        </Box>


       {/* ✅ NEW: Active Alerts Section */}
       {/* {(() => {
          const alerts = alertsData?.data || [];
          
          // DEBUG: Log what we're getting
          console.log('🔍 Active Alerts Debug:', {
            hasAlertsData: !!alertsData,
            alertsDataStructure: alertsData,
            alertsArray: alerts,
            alertsCount: alerts.length
          });

          // TEMP: Always show the card for debugging
          return (
            <Card sx={{
              mb: 3,
              bgcolor: alpha(theme.palette.error.main, 0.05),
              borderLeft: `4px solid ${theme.palette.error.main}`,
              boxShadow: 3,
            }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 48, height: 48 }}>
                    <WarningIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h6" color="error.main" fontWeight="bold">
                      🚨 Active Alerts ({alerts.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {alerts.length === 0 ? 'No alerts found - Check console for debug info' : 'Immediate attention required'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                  >
                    View All
                  </Button>
                </Stack>

                {alerts.length === 0 ? (
                  <Alert severity="info">
                    No active alerts. Check browser console (F12) for debug information.
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {alerts.slice(0, 5).map((alert: any) => (
                      <Alert
                        key={alert.id}
                        severity={alert.severity}
                        icon={
                          alert.type === 'maintenance' ? <Engineering fontSize="small" /> :
                          alert.type === 'speeding' ? <SpeedIcon fontSize="small" /> :
                          alert.type === 'geofence' ? <LocationOn fontSize="small" /> :
                          <WarningIcon fontSize="small" />
                        }
                        action={
                          <Button size="small" color="inherit">
                            {alert.action}
                          </Button>
                        }
                      >
                        <Typography variant="body2">
                          <strong>{alert.vehicle}</strong>: {alert.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(alert.timestamp).toLocaleString()}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                )}

                {alerts.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    +{alerts.length - 5} more alerts
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })()} */}

        {/* Enhanced Fleet Summary Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}></Grid>



        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
          {/* Total Vehicles */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <CarIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Vehicles
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {devices.length}
                    </Typography>
                    <Chip
                      label="Active in fleet"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Healthy Vehicles */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.success.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                    <CheckCircleOutline fontSize="large" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Healthy Vehicles
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {engine?.filter((e: any) => e.status === 'healthy').length || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      {(engine?.filter((e: any) => e.status === 'warning').length || 0) > 0 && (
                        <Chip
                          label={`${engine?.filter((e: any) => e.status === 'warning').length} warnings`}
                          size="small"
                          color="warning"
                          icon={<WarningIcon fontSize="small" />}
                        />
                      )}
                      {(engine?.filter((e: any) => e.status === 'critical').length || 0) > 0 && (
                        <Chip
                          label={`${engine?.filter((e: any) => e.status === 'critical').length} critical`}
                          size="small"
                          color="error"
                          icon={<WarningIcon fontSize="small" />}
                        />
                      )}
                    </Box>
                  </Box>
                </Stack>
                {/* Health Bar */}
                <LinearProgress
                  variant="determinate"
                  value={devices.length > 0 ? Math.round((engine?.filter((e: any) => e.status === 'healthy').length || 0) / devices.length * 100) : 0}
                  sx={{
                    mt: 2,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'success.main',
                      borderRadius: 4,
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {devices.length > 0 ? Math.round((engine?.filter((e: any) => e.status === 'healthy').length || 0) / devices.length * 100) : 0}% fleet health score
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Mileage */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.info.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                    <SpeedIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Mileage (14d)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="info.main">
                      {Math.round(mileage?.fleetDaily?.reduce((sum: number, day: any) => sum + (day.totalMileage || 0), 0) || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      km traveled
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Avg per vehicle
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {devices.length > 0 ? Math.round((mileage?.fleetDaily?.reduce((sum: number, day: any) => sum + (day.totalMileage || 0), 0) || 0) / devices.length) : 0} km
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Avg speed
                      </Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {summary?.mileage?.avgSpeed || 0} km/h
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Idle Time & Efficiency */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.warning.main, 0.1)}`,
            }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                    <TimelapseIcon fontSize="large" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Idle Time (14d)
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="warning.main">
                      {Math.round((idle?.reduce((sum: number, d: any) => sum + (d.totalIdleMinutes || 0), 0) || 0) / 60)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      hours idling
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Total trips
                      </Typography>
                      <Chip
                        label={idle?.reduce((sum: number, d: any) => sum + (d.tripCount || 0), 0) || 0}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Dashboard Widgets */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Engine Health */}
          <Grid item xs={12} lg={4}>
            <Card sx={{
              height: { xs: 400, md: 420 },
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                    <EngineIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Engine Health
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Real-time diagnostics
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Live"
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon fontSize="small" />}
                />
              </Box>
              <Box sx={{ p: 2, height: 'calc(100% - 85px)', minHeight: 250, overflow: 'auto' }}>
                <RealEngineHealthWidget data={engine} />
              </Box>
            </Card>
          </Grid>

          {/* Fleet Mileage Chart */}
          <Grid item xs={12} lg={4}>
            <Card sx={{
              height: { xs: 400, md: 420 },
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <BarChartIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Fleet Mileage
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 14 days trend
                    </Typography>
                  </Box>
                </Box>
                {(mileage?.totalMileage || 0) > 0 && (
                  <Chip
                    icon={<TrendingUp fontSize="small" />}
                    label="+12%"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box sx={{ p: 2, height: 'calc(100% - 85px)', overflow: 'auto' }}>
                <RealFleetMileageChart data={mileage} />
              </Box>
            </Card>
          </Grid>

          {/* Fuel Consumption */}
          <Grid item xs={12} lg={4}>
            <Card sx={{
              height: { xs: 400, md: 420 },
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                    <FuelIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Fuel Consumption
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Usage trend analysis
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, height: 'calc(100% - 85px)', overflow: 'auto' }}>
                <RealFuelTrendChart data={fuel} />
              </Box>
            </Card>
          </Grid>

           {/* Speed Analytics - NEW */}
           <Grid item xs={12} lg={6}>
            <Card sx={{
              height: { xs: 400, md: 420 },
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                    <SpeedIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Speed Analytics
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fleet speed trends and violations
                    </Typography>
                  </Box>
                </Box>
                {(dashboardData as any)?.speed?.summary?.totalViolations > 0 && (
                  <Chip
                    icon={<WarningIcon fontSize="small" />}
                    label={`${(dashboardData as any).speed.summary.totalViolations} violations`}
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
              <Box sx={{ p: 2, height: 'calc(100% - 85px)', overflow: 'auto' }}>
              <SpeedAnalyticsChart data={(dashboardData as any)?.speed} />
              </Box>
            </Card>
          </Grid>


          {/* Idle Time Analysis */}
          {/* <Grid item xs={12} lg={6}>
            <Card sx={{
              height: { xs: 400, md: 420 },
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                    <TimelapseIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Idle Time Analysis
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Efficiency metrics
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, height: 'calc(100% - 85px)', overflow: 'auto' }}>
                <RealIdleTimeWidget data={idle} />
              </Box>
            </Card>
          </Grid> */}

          {/* Driver Safety Scorecard */}
          <Grid item xs={12} lg={6}>
            <Card sx={{
              height: { xs: 400, md: 420 },
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                    <Commute />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Driver Safety
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Performance scorecard
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2, height: 'calc(100% - 85px)', overflow: 'auto' }}>
                <RealDriverSafetyScorecard engineHealthData={engine} />
              </Box>
            </Card>
          </Grid>

          {/* Geofence Alerts - Full Width */}
          <Grid item xs={12}>
            <Card sx={{
              boxShadow: 3,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6,
              }
            }}>
              <Box sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <LocationOn />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Geofence Violations
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Real-time location monitoring
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Last 24 hours"
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ p: 2 }}>
                <GeofenceAlertsWidget />
              </Box>
            </Card>
            </Grid>
        </Grid>

     {/* ✅ NEW: Vehicle Performance Table */}
   

         {/* Add Device Dialog */}
         <AddDeviceDialog
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onAdded={(created: Device) => {
            if (created.status === 'online') {
              setSelectedDevice(created);
            }
            refetch();
          }}
        />
      </Box>
    </Fade>
  );
};

export default Dashboard;